import { useQuery } from '@apollo/client';
import { CompaniesNav } from '@clearkit/icons';
import classNames from 'classnames';
import { CKLayoutHorizontal } from 'clearkit';
import { useEffect, useState } from 'react';

import { ItemCount } from '~/components/ListItem/ItemCount';
import { PageLayout } from '~/components/PageLayout';
import {
  MAX_COUNT_HIDE,
  MAX_COUNT_HIDE_LABEL,
} from '~/components/prospector/useProspector/constants';

import { Search } from '../hooks/useTargetMarket';
import TargetMarketCompaniesQuery from '../queries/TargetMarketCompanies.graphql';
import { SAMPLE_COMPANIES } from '../Sidebar/logic';
import { TargetMarketsCompany } from '../types';
import { CompanyItem } from './CompanyItem';
import { EmptyList } from './EmptyList';

const PLACEHOLDER_COMPANY: TargetMarketsCompany = {
  id: '',
  domain: '',
  name: 'Placeholder',
  country: 'Placeholder',
  industry: 'Placeholder',
  employeesRange: 'Placeholder',
  linkedin: 'Placeholder',
  twitter: 'Placeholder',
  crunchbase: 'Placeholder',
};

const CompaniesList = ({ search }: { search: Search | null }) => {
  const [previousTotalCompanies, setPreviousTotalCompanies] = useState(0);

  const { data } = useQuery(TargetMarketCompaniesQuery, {
    skip: !search,
    variables: {
      country: search?.country || [],
      description: search?.description || [],
      employees: search?.employees || [],
      industry: search?.industry || [],
      raised: search?.raised || [],
      tag: search?.tag || [],
      tech: search?.tech || [],
      type: search?.type || [],
    },
  }) as {
    data?: {
      targetMarketCompanies?: {
        total: number;
        companies: TargetMarketsCompany[];
      };
    };
  };
  const showPlaceholder = search === null;

  useEffect(() => {
    if (data) {
      setPreviousTotalCompanies(data?.targetMarketCompanies?.total || 0);
    }
  }, [data]);

  const loading = data === undefined;
  const total = data?.targetMarketCompanies?.total || 0;

  const companies: TargetMarketsCompany[] = showPlaceholder
    ? SAMPLE_COMPANIES
    : loading
    ? Array(20).fill(PLACEHOLDER_COMPANY)
    : data?.targetMarketCompanies?.companies || [];

  return (
    <PageLayout className="@container/list">
      <PageLayout.HeaderWrapper className="py-3 pb-3 xl:py-3">
        <CKLayoutHorizontal>
          <ItemCount
            end={total}
            hideCountAbove={MAX_COUNT_HIDE}
            hideCountLabel={MAX_COUNT_HIDE_LABEL}
            label={total === 1 ? 'company' : 'companies'}
            start={previousTotalCompanies}
          >
            <CompaniesNav height={18} width={18} />
          </ItemCount>
        </CKLayoutHorizontal>
      </PageLayout.HeaderWrapper>
      <PageLayout.Body
        className={classNames('relative', {
          'overflow-hidden': showPlaceholder,
        })}
      >
        <PageLayout.Content>
          <EmptyList isVisible={showPlaceholder} />

          {!loading && companies.length === 0 ? (
            <div className="px-6 py-40 text-center bg-white">
              <p className="text-center">
                No companies found, please try
                <br />
                updating your filters.
              </p>
            </div>
          ) : (
            <div className="space-y-6 @container/list">
              {companies.map((company, index) => (
                <CompanyItem
                  company={company}
                  key={`${company.domain}${index.toString()}`}
                  loading={showPlaceholder ? false : loading}
                  number={index + 1}
                />
              ))}
            </div>
          )}
        </PageLayout.Content>
      </PageLayout.Body>
    </PageLayout>
  );
};

export default CompaniesList;
